import { createAsyncThunk } from "@reduxjs/toolkit";

import { assistantService } from "../services/assistant.service";
import {
  addAttachment,
  appendDelta,
  appendUserMessage,
  failAssistantMessage,
  finishAssistantMessage,
  removeLastExchange,
  restoreMessages,
  setEnabled,
  setRestoring,
  setSessionId,
  setToolStatus,
  startAssistantMessage,
} from "../slices/assistant.slice";

/** Probes whether the backend has the assistant configured; hides the widget if not. */
export const checkAssistantAvailability = createAsyncThunk(
  "assistant/checkAvailability",
  async (_: void, { dispatch }) => {
    const enabled = await assistantService.getStatus();
    dispatch(setEnabled(enabled));
    return enabled;
  },
);

/** Restores the conversation held server-side for this session, after a reload. */
export const restoreAssistantConversation = createAsyncThunk(
  "assistant/restoreConversation",
  async (_: void, { dispatch, getState }) => {
    const { sessionId, messages } = (getState() as any).assistant;
    if (!sessionId || messages.length) return;

    dispatch(setRestoring(true));
    const history = await assistantService.fetchHistory(sessionId);
    dispatch(restoreMessages(history));
  },
);

/**
 * The in-flight request, so the user can stop a reply mid-stream.
 * Module scope rather than store state — an AbortController is not serialisable.
 */
let inFlight: AbortController | null = null;

export const stopAssistantResponse = (): void => {
  inFlight?.abort();
  inFlight = null;
};

interface SendMessageArgs {
  message: string;
  currentPath: string;
}

export const sendAssistantMessage = createAsyncThunk(
  "assistant/sendMessage",
  async ({ message, currentPath }: SendMessageArgs, { dispatch, getState }) => {
    const trimmed = message.trim();
    if (!trimmed) return;

    const { sessionId } = (getState() as any).assistant;

    dispatch(appendUserMessage(trimmed));
    dispatch(startAssistantMessage());

    const controller = new AbortController();
    inFlight = controller;

    // Text arrives token by token. Dispatching each one would re-render the thread
    // on every token, so deltas are coalesced and flushed once per animation frame.
    let buffer = "";
    let frame: number | null = null;

    const flush = () => {
      frame = null;
      if (!buffer) return;
      dispatch(appendDelta(buffer));
      buffer = "";
    };

    const queueFlush = () => {
      if (frame === null) frame = requestAnimationFrame(flush);
    };

    let failed = false;

    try {
      await assistantService.streamChat({
        message: trimmed,
        sessionId,
        currentPath,
        signal: controller.signal,
        onEvent: (event) => {
          switch (event.type) {
            case "session":
              dispatch(setSessionId(event.sessionId));
              break;
            case "text":
              buffer += event.delta;
              queueFlush();
              break;
            case "tool":
              flush();
              if (frame !== null) {
                cancelAnimationFrame(frame);
                frame = null;
              }
              dispatch(setToolStatus(event.label));
              break;
            case "ui":
              flush();
              dispatch(addAttachment(event.payload));
              break;
            case "error":
              failed = true;
              flush();
              dispatch(failAssistantMessage(event.message));
              break;
            case "done":
            default:
              break;
          }
        },
      });
    } catch (error) {
      // An abort is the user pressing stop, not a failure.
      if (!controller.signal.aborted) {
        failed = true;
        dispatch(
          failAssistantMessage(
            "I lost the connection. Please check your network and try again.",
          ),
        );
      }
    } finally {
      if (frame !== null) cancelAnimationFrame(frame);
      flush();
      if (inFlight === controller) inFlight = null;
    }

    if (!failed) dispatch(finishAssistantMessage());
  },
);

/** Re-sends the last question after a failure, replacing the failed exchange. */
export const retryAssistantMessage = createAsyncThunk(
  "assistant/retryMessage",
  async (currentPath: string, { dispatch, getState }) => {
    const { lastQuestion } = (getState() as any).assistant;
    if (!lastQuestion) return;
    dispatch(removeLastExchange());
    await dispatch(sendAssistantMessage({ message: lastQuestion, currentPath }));
  },
);

export const resetAssistantConversation = createAsyncThunk(
  "assistant/resetConversation",
  async (_: void, { getState }) => {
    const { sessionId } = (getState() as any).assistant;
    if (sessionId) await assistantService.resetSession(sessionId);
  },
);
